// src/components/kanban/DynamicKanbanColumn.tsx
/**
 * Colonna della Kanban board dinamica (Planner-style): elenco task attivi trascinabili
 * (SortableContext locale alla colonna, non condiviso con le altre) più sezione
 * "Completate" collassabile con card non trascinabili.
 */
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  ActionIcon,
  Badge,
  Box,
  Collapse,
  Group,
  Menu,
  Paper,
  ScrollArea,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconDotsVertical, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { AUTO_ASSIGN_CATEGORY, type DynamicKanbanColumnData, type ProjectTaskSummary } from '../../types/api';
import { columnDndId, splitColumnTasks, taskDndId } from './board-utils';
import SortableTaskCard from './SortableTaskCard';
import TaskCardView from './TaskCardView';

interface DynamicKanbanColumnProps {
  column: DynamicKanbanColumnData;
  showCovers: boolean;
  isCompletedExpanded: boolean;
  onToggleCompleted: () => void;
  onTaskClick: (taskId: number) => void;
  onToggleTaskComplete: (task: ProjectTaskSummary) => void;
  onDeleteTask: (task: ProjectTaskSummary) => void;
  onAddTask: (columnId: number) => void;
  onEditColumn: (column: DynamicKanbanColumnData) => void;
  onDeleteColumn: (column: DynamicKanbanColumnData) => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  [AUTO_ASSIGN_CATEGORY.Gamma]: 'indigo',
  [AUTO_ASSIGN_CATEGORY.Personalizzati]: 'teal',
};

export default function DynamicKanbanColumn({
  column,
  showCovers,
  isCompletedExpanded,
  onToggleCompleted,
  onTaskClick,
  onToggleTaskComplete,
  onDeleteTask,
  onAddTask,
  onEditColumn,
  onDeleteColumn,
}: DynamicKanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: columnDndId(column.id),
    data: { type: 'column', columnId: column.id },
  });

  const { active, completed } = splitColumnTasks(column);
  const hiddenCount = column.hiddenCompletedCount ?? 0;

  return (
    <Box
      ref={setNodeRef}
      style={{
        minWidth: 'min(300px, 85vw)',
        maxWidth: '340px',
        width: '100%',
        height: '100%',
      }}
    >
      <Paper
        p="sm"
        withBorder
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: isOver ? `2px dashed ${column.color}` : undefined,
          backgroundColor: isOver ? `${column.color}12` : undefined,
          transition: 'background-color 0.15s ease',
        }}
      >
        <Group justify="space-between" mb="sm" wrap="nowrap">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Box style={{ width: 4, height: 20, backgroundColor: column.color, borderRadius: 2, flexShrink: 0 }} />
            <Text fw={600} size="sm" lineClamp={1}>
              {column.name}
            </Text>
            <Badge variant="light" color="gray" size="sm">
              {active.length}
            </Badge>
            {column.autoAssignCategory && (
              <Badge variant="dot" size="sm" color={CATEGORY_COLORS[column.autoAssignCategory] ?? 'gray'}>
                {column.autoAssignCategory}
              </Badge>
            )}
          </Group>

          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm">
                <IconDotsVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPlus size={14} />} onClick={() => onAddTask(column.id)}>
                Aggiungi task
              </Menu.Item>
              <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEditColumn(column)}>
                Modifica colonna
              </Menu.Item>
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDeleteColumn(column)}>
                Elimina colonna
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>

        {column.description && (
          <Text size="xs" c="dimmed" mb="sm" lineClamp={2}>
            {column.description}
          </Text>
        )}

        <ScrollArea style={{ flex: 1 }} scrollbarSize={8}>
          <SortableContext items={active.map((t) => taskDndId(t.id))} strategy={verticalListSortingStrategy}>
            <Stack gap="sm" mih={60}>
              {active.length === 0 ? (
                <Text c="dimmed" size="sm" ta="center" py="md">
                  Nessun task
                </Text>
              ) : (
                active.map((task) => (
                  <SortableTaskCard
                    key={task.id}
                    task={task}
                    showCover={showCovers}
                    onClick={() => onTaskClick(task.id)}
                    onToggleComplete={() => onToggleTaskComplete(task)}
                    onDelete={() => onDeleteTask(task)}
                  />
                ))
              )}
            </Stack>
          </SortableContext>

          {(completed.length > 0 || hiddenCount > 0) && (
            <Stack gap="xs" mt="sm">
              <UnstyledButton onClick={onToggleCompleted}>
                <Group gap={4}>
                  {isCompletedExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                  <Text size="sm" c="dimmed">
                    Completate ({completed.length})
                  </Text>
                </Group>
              </UnstyledButton>
              <Collapse in={isCompletedExpanded}>
                <Stack gap="sm">
                  {completed.map((task) => (
                    <TaskCardView
                      key={task.id}
                      task={task}
                      showCover={showCovers}
                      completed
                      onClick={() => onTaskClick(task.id)}
                      onToggleComplete={() => onToggleTaskComplete(task)}
                      onDelete={() => onDeleteTask(task)}
                    />
                  ))}
                  {hiddenCount > 0 && (
                    <Text size="xs" c="dimmed" ta="center">
                      +{hiddenCount} completate più vecchie di 90 giorni (usa il filtro periodo per vederle)
                    </Text>
                  )}
                </Stack>
              </Collapse>
            </Stack>
          )}
        </ScrollArea>
      </Paper>
    </Box>
  );
}
